import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircleHeart, TrendingDown } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useSiteSettings } from "@/context/SettingsContext";
import { CalcInputs } from "@/components/home/calculator/CalcInputs";
import { CalcResults } from "@/components/home/calculator/CalcResults";
import { CalcChart } from "@/components/home/calculator/CalcChart";
import { CalcTable } from "@/components/home/calculator/CalcTable";
import { compute, pctLabel, savingsPcts } from "@/components/home/calculator/calc";

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.35 } };

const Empty = () => (
  <motion.div key="empty" {...fade} className="rounded-2xl border-2 border-dashed border-border p-8 text-center grid place-items-center gap-3 h-full min-h-48" data-testid="calculator-empty-state">
    <TrendingDown className="h-8 w-8 text-primary" />
    <p className="font-display text-lg font-bold">Enter what you pay per lb to see your savings.</p>
  </motion.div>
);

export const SavingsCalculator = () => {
  const { settings } = useSiteSettings();
  const [price, setPrice] = useState("");
  const [lbs, setLbs] = useState(10);
  const [tierKey, setTierKey] = useState("bronze");
  const tiers = settings?.tiers || [];
  const tier = tiers.find((t) => t.key === tierKey) || tiers[0] || { name: "", price: 0 };
  const pcts = savingsPcts(settings);
  const p = Number(price);
  const has = p > 0;
  const r = has ? compute(p, lbs, tier.price, pcts) : null;
  const g = settings?.guaranteed_min_pct ?? 20;
  return (
    <section id="savings-calculator" className="py-24 sm:py-32 bg-secondary/50" data-testid="savings-calculator-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow text-amber-500">Savings calculator</p>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">See what you could be saving.</h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-8 flex items-start gap-4 rounded-3xl border border-border bg-card p-6" data-testid="calculator-callout">
            <span className="h-11 w-11 shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center"><MessageCircleHeart className="h-5 w-5 text-white" /></span>
            <p className="text-base leading-relaxed"><span className="font-bold">Our guarantee:</span> we save our buyers at least {g}% on every purchase deal. Plug in what you're paying now and see what you'd save.</p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-6 rounded-3xl border border-border bg-card p-6 sm:p-8 grid gap-8 md:grid-cols-[1fr_1.3fr]">
            <CalcInputs price={price} setPrice={setPrice} lbs={lbs} setLbs={setLbs} tier={tierKey} setTier={setTierKey} tiers={tiers} />
            <AnimatePresence mode="wait">
              {has ? <motion.div key="results" {...fade}><CalcResults r={r} tierName={tier.name} /></motion.div> : <Empty />}
            </AnimatePresence>
          </div>
          <AnimatePresence>
            {has && (
              <motion.div key="viz" {...fade} className="mt-6 rounded-3xl border border-border bg-card p-6 sm:p-8 space-y-6" data-testid="calculator-visuals">
                <div>
                  <p className="text-sm font-bold">Paying now vs. through I'm The Plug Now <span className="text-primary">({pctLabel(pcts)} savings)</span></p>
                  <CalcChart price={p} lbs={lbs} pcts={pcts} />
                </div>
                <div>
                  <p className="text-sm font-bold mb-3">Quick-glance savings</p>
                  <CalcTable price={p} fee={tier.price} pcts={pcts} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <p className="mt-4 text-xs text-muted-foreground" data-testid="calculator-disclaimer">Savings shown are estimates based on our guaranteed minimum. Actual pricing depends on farm, product, and volume.</p>
        </Reveal>
      </div>
    </section>
  );
};
